import React, { useLayoutEffect, useRef, useState } from 'react';
import { motion, useScroll, useSpring, useTransform, useVelocity } from 'framer-motion';
import Icon from './Icon';
import ProjectLightbox from './ProjectLightbox';
import { projectMedia } from '../data/site';
import { SectionHead, ArrowButton } from './ui';

/* One cinematic slide: photo with inner parallax + title overlay */
function Slide({ p, i, total, x, onOpen }) {
  const media = projectMedia(p);
  const ref = useRef(null);
  // inner image drifts opposite to the track for a parallax feel
  const imgX = useTransform(x, (v) => `${(v / 18) % 40}px`);
  return (
    <motion.button type="button" ref={ref} className="hs-card" onClick={() => onOpen(p)} aria-label={`Open ${p.title}`}
      initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, delay: Math.min(i, 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}>
      <span className="hs-media">
        <motion.img src={media.coverSm} srcSet={`${media.coverSm} 800w, ${media.cover} 1600w`} sizes="(max-width: 700px) 85vw, 50vw"
          alt={p.title} loading="lazy" decoding="async" style={{ x: imgX }} />
      </span>
      <span className="hs-shade" />
      <span className="hs-no">{String(i + 1).padStart(2, '0')}<em>/{String(total).padStart(2, '0')}</em></span>
      <span className="hs-info">
        <span className="ph-cat">{p.category}</span>
        <span className="hs-title">{p.title}</span>
        <span className="hs-loc"><Icon name="pin" size={14} />{p.location}{p.status ? ` · ${p.status}` : ''}</span>
      </span>
      <span className="ph-view"><Icon name="arrow" size={20} /></span>
    </motion.button>
  );
}

/* Pinned section on the home page: scrolling down moves the real project photos sideways */
export default function HorizontalProjects({ projects, num = '03' }) {
  const list = projects.filter((p) => projectMedia(p));
  const wrap = useRef(null);
  const track = useRef(null);
  const [dist, setDist] = useState(0);
  const [open, setOpen] = useState(null);

  useLayoutEffect(() => {
    const measure = () => { if (track.current) setDist(Math.max(0, track.current.scrollWidth - window.innerWidth + 40)); };
    measure();
    const ro = new ResizeObserver(measure);
    if (track.current) ro.observe(track.current);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [list.length]);

  const { scrollYProgress } = useScroll({ target: wrap, offset: ['start start', 'end end'] });
  const smooth = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.4 });
  const x = useTransform(smooth, [0, 1], [0, -dist]);
  const skew = useTransform(useSpring(useVelocity(x), { stiffness: 200, damping: 40 }), [-2000, 2000], [4, -4]);

  if (!list.length) return null;
  return (
    <section ref={wrap} className="hscroll section-hero" style={{ height: `calc(100vh + ${dist}px)` }}>
      <div className="hscroll-sticky">
        <div className="container">
          <div className="head-row hscroll-head">
            <SectionHead num={num} kicker="Selected work" title="Projects we *manage & build*" />
            <ArrowButton to="/portfolio">View all projects</ArrowButton>
          </div>
        </div>
        <motion.div ref={track} className="hscroll-track" style={{ x, skewX: skew }}>
          {list.map((p, i) => <Slide key={p.id} p={p} i={i} total={list.length} x={x} onOpen={setOpen} />)}
          <div className="hcard-end">
            <div>
              <h3>Your project<br />could be next.</h3>
              <ArrowButton to="/request-consultation" variant="white">Start a conversation</ArrowButton>
            </div>
          </div>
        </motion.div>
        <div className="hscroll-progress"><motion.i style={{ scaleX: smooth }} /></div>
      </div>
      <ProjectLightbox project={open} onClose={() => setOpen(null)} />
    </section>
  );
}
